import React, {
  MemoExoticComponent,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import CarouselDotsComponent from '../carousel-dots.component';
import FixedSizeList from './fixed-size-list/FixedSizeList';
import ImageComponent from '@spa/components/image-component/image.component';
import InfiniteLoader from 'react-window-infinite-loader';
import { List } from './fixed-size-list/ListComponent';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { generateStyleTag } from './generateVirtualCarouselStyle';
import memoize from 'memoize-one';
import styles from './virtualized-carousel.component.module.scss';
import useIsCSR from '@spa/lib/react/hooks/useIsCSR';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';

export interface SizesPerBreakpointEntry {
  readonly minViewportWidth: number;
  readonly height: number;
  readonly itemWidth: number;
  readonly maxWidth: number;
}

interface ItemData<Item> {
  readonly items: Item[];
}

interface ColumnProps<Item> {
  readonly data: ItemData<Item>;
  readonly index: number;
  readonly style: React.CSSProperties;
  readonly className?: string;
  readonly cssId?: string;
}

interface CarouselDimensions {
  readonly itemWidth: number;
  readonly offsetWidth: number;
}

export interface Props<Item> {
  readonly componentId: string;
  readonly items: Item[];
  readonly sizesPerBreakpointEntries: SizesPerBreakpointEntry[];
  readonly column: MemoExoticComponent<(props: ColumnProps<Item>) => ReactElement | null>;
  readonly loadMoreItems: (startIndex: number, stopIndex: number) => void;
  readonly isItemLoaded: (index: number) => boolean;
  readonly navigation: boolean;
  readonly title?: string;
  readonly limit?: number;
}

// Function used to make the animation smooth https://github.com/bvaughn/react-window/issues/38
const easeInOutQuint = (t: number): number => {
  return t < 0.5 ? 16 * t ** 5 : 1 + 16 * (t - 1) ** 5;
};

const getLastSizesEntry = (
  sizesPerBreakpointEntries: SizesPerBreakpointEntry[]
): SizesPerBreakpointEntry => {
  return sizesPerBreakpointEntries[sizesPerBreakpointEntries.length - 1];
};

const getActualSizesEntry = (
  sizesPerBreakpointEntries: SizesPerBreakpointEntry[],
  windowWidth: number
): SizesPerBreakpointEntry => {
  const sizesEntriesFiltered: Array<SizesPerBreakpointEntry> = sizesPerBreakpointEntries.filter(
    (entry: SizesPerBreakpointEntry): boolean => {
      return entry.minViewportWidth <= windowWidth;
    }
  );

  return sizesEntriesFiltered.length > 0
    ? sizesEntriesFiltered[sizesEntriesFiltered.length - 1]
    : getLastSizesEntry(sizesPerBreakpointEntries);
};

function VirtualizedCarouselComponent<Item>({
  componentId,
  sizesPerBreakpointEntries,
  items,
  column,
  loadMoreItems,
  isItemLoaded,
  navigation,
  title,
  limit
}: Props<Item>): ReactElement {
  const defaultSizesEntry = getLastSizesEntry(sizesPerBreakpointEntries);
  const { formatMessage }: NamespacedIntl = useNamespacedIntl('carousel');
  const listRef = useRef<List<Item>>(null);
  const swiperRef = useRef<HTMLDivElement>(null);
  const [currentItem, setCurrentItem] = useState<number>(0);
  const [currentScrollOffset, setCurrentScrollOffset] = useState<number>(0);
  const [scrolling, setScrolling] = useState<NodeJS.Timeout>();
  const [dimensions, setDimensions] = useState<CarouselDimensions>({
    itemWidth: defaultSizesEntry.itemWidth,
    offsetWidth: defaultSizesEntry.maxWidth
  });
  const isCsr = useIsCSR();
  const createItemData = useMemo(() => {
    return memoize((items: Array<Item>) => ({
      items
    }));
  }, []);
  const cssId: string = componentId.replaceAll(':', '');
  const styleTag: ReactElement | null = useMemo(() => {
    const swiperStyle: ReactElement = generateStyleTag(
      cssId,
      sizesPerBreakpointEntries.map(
        ({ minViewportWidth, height, maxWidth }: SizesPerBreakpointEntry) => {
          return {
            minViewportWidth,
            height,
            maxWidth
          };
        }
      ),
      styles['virtualized-carousel__swiper']
    );

    const itemsStyle: ReactElement = generateStyleTag(
      cssId,
      sizesPerBreakpointEntries.map(
        ({ minViewportWidth, height, maxWidth, itemWidth }: SizesPerBreakpointEntry) => {
          return {
            minViewportWidth,
            height,
            itemProperties: { width: itemWidth, gap: 0 },
            maxWidth
          };
        }
      ),
      styles['virtualized-carousel__item'],
      !isCsr
    );

    return (
      <>
        {swiperStyle}
        {itemsStyle}
      </>
    );
  }, [sizesPerBreakpointEntries, cssId, isCsr]);

  if (sizesPerBreakpointEntries.length === 0) {
    throw new Error('sizesPerBreakpointEntries should be provided to VirtualizedCarouselComponent');
  }

  const itemData: ItemData<Item> = createItemData([...items]);
  const itemCount: number = limit && itemData.items.length > limit ? limit : itemData.items.length;

  const syncDimensions = useCallback(() => {
    const actualSize: SizesPerBreakpointEntry = getActualSizesEntry(
      sizesPerBreakpointEntries,
      window.innerWidth
    );
    setDimensions({
      itemWidth: actualSize.itemWidth,
      offsetWidth: swiperRef.current?.offsetWidth ?? actualSize.maxWidth
    });
  }, [sizesPerBreakpointEntries]);
  const handleSwiperRef = useCallback(
    (node: HTMLDivElement | null): void => {
      swiperRef.current = node;
      if (node) {
        syncDimensions();
      }
    },
    [syncDimensions]
  );
  useEffect(() => {
    function handleResize(): void {
      syncDimensions();
    }

    window.addEventListener('resize', handleResize);
    return (): void => {
      window.removeEventListener('resize', handleResize);
    };
  }, [syncDimensions]);

  const goToNearestItem = (scrollOffset: number): void => {
    const itemIndex: number = Math.round(scrollOffset / dimensions.itemWidth);
    const onAnimationCompleted = (): void => {
      clearTimeout(scrolling);
      calculateCurrentTile(scrollOffset);
    };
    animatedScroll(itemIndex, onAnimationCompleted);
  };

  const isLastTileVisible = (): boolean => {
    const lastItemIdx: number = itemCount - dimensions.offsetWidth / dimensions.itemWidth;
    const lastItemIdxRounded: number = Math.round(lastItemIdx);
    return lastItemIdx > lastItemIdxRounded
      ? currentItem > lastItemIdxRounded
      : currentItem >= lastItemIdxRounded;
  };

  const animateScrollToItem = (newCurrentItem: number): void => {
    setCurrentItem(newCurrentItem);
    animatedScroll(newCurrentItem);
  };

  const handlePrev = (): void => {
    animateScrollToItem(currentItem - 1);
  };

  const handleNext = (): void => {
    animateScrollToItem(currentItem + 1);
  };

  const handleNav = (navIdx: number): void => {
    animateScrollToItem(navIdx);
  };

  const calculateCurrentTile = (scrollOffset: number): void => {
    const itemIndex: number = Math.round(scrollOffset / dimensions.itemWidth);
    setCurrentItem(itemIndex);
  };

  const onScrollStop = (callback: () => void): void => {
    if (scrolling) {
      clearTimeout(scrolling);
    }
    setScrolling(
      setTimeout(() => {
        callback();
      }, 200)
    );
  };

  const animatedScroll = (scrollToItem: number, onAnimationCompleted?: () => void): void => {
    const scrollOffsetInitial: number = currentScrollOffset;
    const scrollOffsetFinal: number = scrollToItem * dimensions.itemWidth;
    animate(scrollOffsetInitial, scrollOffsetFinal, undefined, onAnimationCompleted);
  };

  const animate = (
    scrollOffsetInitial: number,
    scrollOffsetFinal: number,
    animationStartTime: number | undefined,
    onAnimationCompleted?: () => void
  ): void => {
    requestAnimationFrame(() => {
      const duration: number = 200;
      const now: number = performance.now();
      const effectiveAnimationStartTime: number = animationStartTime ?? now;
      const elapsed: number = now - effectiveAnimationStartTime;
      const scrollDelta: number = scrollOffsetFinal - scrollOffsetInitial;
      const easedTime: number = easeInOutQuint(Math.min(1, elapsed / duration));
      const scrollOffset: number = scrollOffsetInitial + scrollDelta * easedTime;

      listRef.current?.scrollTo(scrollOffset);

      if (elapsed < duration) {
        animate(
          scrollOffsetInitial,
          scrollOffsetFinal,
          effectiveAnimationStartTime,
          onAnimationCompleted
        );
      } else if (onAnimationCompleted) {
        onAnimationCompleted();
      }
    });
  };

  const maxNavs: number = itemCount - Math.trunc(dimensions.offsetWidth / dimensions.itemWidth);

  return (
    <div className={styles['virtualized-carousel']}>
      {styleTag}
      <div id={cssId} className={styles['virtualized-carousel__swiper']} ref={handleSwiperRef}>
        <InfiniteLoader
          isItemLoaded={isItemLoaded}
          itemCount={itemCount}
          loadMoreItems={loadMoreItems}
          threshold={2}
        >
          {({ onItemsRendered, ref }): ReactElement => (
            // @ts-expect-error Needs to be a class for InfiniteLoader
            <FixedSizeList
              className={styles['virtualized-carousel__list']}
              height={'100%'}
              itemCount={itemCount}
              itemSize={dimensions.itemWidth}
              itemData={itemData}
              childrenClassName={styles['virtualized-carousel__item']}
              cssId={cssId}
              onItemsRendered={onItemsRendered}
              // @ts-expect-error Method to set reference
              ref={(list: unknown): void => {
                ref(list);
                // @ts-expect-error Set the reference
                listRef.current = list;
              }}
              width={dimensions.offsetWidth}
              layout="horizontal"
              onScroll={(props): void => {
                const scrollOffset = props.scrollOffset;
                setCurrentScrollOffset(scrollOffset);

                if (!props.scrollUpdateWasRequested) {
                  calculateCurrentTile(scrollOffset);
                  onScrollStop(async () => {
                    const isFinalOffset =
                      Math.ceil(scrollOffset) >=
                      dimensions.itemWidth * itemCount - dimensions.offsetWidth;
                    if (scrollOffset !== 0 && !isFinalOffset) {
                      goToNearestItem(scrollOffset);
                    }
                  });
                }
              }}
              direction={'horizontal'}
              overscanCount={2}
              useIsScrolling={false}
            >
              {column}
            </FixedSizeList>
          )}
        </InfiniteLoader>

        {currentItem !== 0 && (
          <button
            className={styles['virtualized-carousel__prev-button']}
            onClick={handlePrev}
            title={`${title} ` + formatMessage('prevButton')}
            aria-label={formatMessage('prevButton')}
            aria-controls={cssId}
          >
            <ImageComponent
              className={styles['virtualized-carousel__prev-button-icon']}
              src={'/assets/icons/arrow_forward.svg'}
              alt={formatMessage('prevButton')}
              width={20}
              height={20}
            />
          </button>
        )}

        {!isLastTileVisible() && (
          <button
            className={styles['virtualized-carousel__next-button']}
            onClick={handleNext}
            title={`${title} ` + formatMessage('nextButton')}
            aria-label={formatMessage('nextButton')}
            aria-controls={cssId}
          >
            <ImageComponent
              className={styles['virtualized-carousel__next-button-icon']}
              src={'/assets/icons/arrow_forward.svg'}
              alt={formatMessage('nextButton')}
              width={20}
              height={20}
            />
          </button>
        )}
      </div>
      {navigation && (
        <div className={styles['virtualized-carousel__navigation-wrapper']}>
          <CarouselDotsComponent
            currentItem={currentItem}
            isCsr={isCsr}
            maxNavigation={maxNavs}
            onClick={(navIndex: number): void => handleNav(navIndex)}
          />
        </div>
      )}
    </div>
  );
}

export default VirtualizedCarouselComponent;
